import { toast } from "react-toastify";
import { FormFieldErrors, ParsedFormFieldErrors } from "../types";
import CryptoJS from 'crypto-js';
import { SavedAddress } from "../types/address";
import { isNativePlatform } from "./platformUtil";
export const textToTitleCase = (text: string): string => {
    return text
        ?.toLowerCase() // Convert entire string to lowercase to standardize
        .split(' ') // Split the string into words
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
        .join(' '); // Join the words back into a single string
};

export const arrayOfUniqueStrings = (arr: string[]): string[] => {
    return Array.from(new Set(arr));
}

export const sanitizeURLs = (text: string): string => {
    const urlRegex = /((https?:\/\/|www\.)[^\s]+)/g; // Regex to match URLs
    return (text ?? '').replace(urlRegex, (url) => {
        let link = url;
        if (!link.startsWith("http")) {
            link = "https://" + link; // Add https if missing
        }
        return `<a href="${link}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
}

export const getErrorMessage = (error: any, defaultMessage: string = 'Something went wrong 😔'): string => {
    if (!error) throw new Error("Not a valid error");
    if (Array.isArray(error) && error.length > 0) {
        return getErrorMessage(error[0]);
    } else if (typeof error === 'string') {
        return error;
    } else if (error.response?.data?.error?.message) {
        return error.response.data.error.message;
    } else if (error.response?.data?.error) {
        return error.response.data.error;
    } else if (error.response?.data?.message) {
        return error.response.data.message;
    } else if (error.message) {
        return error.message;
    } else {
        return defaultMessage;
    }
}

export const parseFormFieldError = (errors: FormFieldErrors): ParsedFormFieldErrors => {
    let errors_: ParsedFormFieldErrors = {};

    if (errors && typeof errors === 'object') {
        for (let [field, errorsArr] of Object.values(errors)) {
            // Return first error related to a field only
            errors_[field] = errorsArr[0];
        }
    }

    return errors_;
}

export const generateUniqueId = (): string => {
    return String(Date.now()); // Generates a unique 36-char ID (UUID v4)
};

export const formatNumber = (number: number): string => {
    const decimals = 2;
    const valueIfNaN = '-';
    const orderSuffixes = ['', 'k', 'M', 'B', 'T'];
    const minOrder = 0;
    const maxOrder = Infinity;

    let x = parseFloat(number.toString());

    if (isNaN(x)) return valueIfNaN;

    let order;
    if (!isFinite(x)) order = 0;
    else {
        const unboundedOrder = Math.floor(Math.log10(Math.abs(x)) / 3);
        order = Math.max(0, minOrder, Math.min(unboundedOrder, maxOrder, orderSuffixes.length - 1));
    }

    const orderSuffix = orderSuffixes[order];
    if (order !== 0) x /= Math.pow(10, order * 3);

    const style: '' | '$' = ''; // Declare the type explicitly here
    return (
        (style !== '' ? '$' : '') +
        x.toLocaleString('en-US', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals,
        }) +
        orderSuffix
    );
};

export const toValidNumber = (input: any): number => {
    // Define invalid values
    const invalidValues = [undefined, null, 0, "", "-"];

    // Return 0 if input is in the invalid values
    if (invalidValues.includes(input)) {
        return 0;
    }

    // Convert input to a number
    const num = Number(input);

    // Return valid number or 0 if conversion fails (NaN case)
    return isNaN(num) ? 0 : num;
}

export const isValidMobileNumber = (number: string): boolean => {
    if (!number || number.length !== 10 || isNaN(Number(number))) return false;
    return true;
}


export const isValidOTP = (otp: string[], digitsCount = 6): boolean => {
    const otpString = otp.join('').trim();
    if (otpString.length === digitsCount) {
        return true;
    }
    return false;
}

export const loadScript = (src: string): Promise<boolean> => {
    return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};


export const isFresh = (fetched_at: number | null, staleTime: number): boolean => {
    if (!fetched_at) return false;
    return (Date.now() < fetched_at + staleTime);
}

export const isValidURL = (text: string): boolean => {
    try {
        const url = new URL(text);
        if (!url) throw new Error("Not valid url");
        return true;
    } catch (error) {
        return false;
    }
}

export const copyToClipboard = async (text: string, toastMsg: string | null = null) => {
    try {
        await navigator.clipboard.writeText(text);
        if (toastMsg)
            toast.success(toastMsg);
    } catch (error) {
        console.log(error);
    }
}

export const getUserMobileOS = () => {
    const userAgent = navigator.userAgent || navigator.vendor;

    if (/android/i.test(userAgent)) {
        return "Android";
    }

    if (/iPad|iPhone|iPod/.test(userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
        return "iOS";
    }

    return "Unknown";
}

export const isInstagramBrowser = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return userAgent.includes('instagram');
}

export const formatMediaTypes = (content_media_types: any) =>
    Object.entries(content_media_types)
        .map(([type, count]) => {
            const num = Number(count);
            if (!num) return null;

            let label = "";
            switch (type) {
                case "REELS":
                    label = num === 1 ? "Reel" : "Reels";
                    break;
                case "FEED":
                    label = num === 1 ? "Post" : "Posts";
                    break;
                case "STORY":
                    label = num === 1 ? "Story" : "Stories";
                    break;
            }

            return `${num} ${label}`;
        })
        .filter(Boolean)
        .join(", ");


export const getMediaTypeFromURL = (url: string): 'image' | 'video' | 'audio' | 'document' | 'unknown' => {
    const extension = url.split('?')[0].split('.').pop()?.toLowerCase();

    if (!extension) return 'unknown';

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'flv', 'mkv', 'm4v'];
    const audioExtensions = ['mp3', 'wav', 'aac', 'flac', 'm4a', 'oga'];
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];

    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    if (audioExtensions.includes(extension)) return 'audio';
    if (documentExtensions.includes(extension)) return 'document';

    return 'unknown';
}

export function formatBytes(bytes: number | string | undefined | null, decimals = 2): string {
    if (!bytes) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
    const i = Math.floor(Math.log(Number(bytes)) / Math.log(k))

    const size = parseFloat((Number(bytes) / Math.pow(k, i)).toFixed(decimals))
    return `${size} ${sizes[i]}`
}

export const getStatusColorClass = (status: string) => {
    switch (status) {
        case "pending":
            return "text-warning";
        case "accepted":
            return "text-success";
        case "rejected":
            return "text-danger";
        case 'paid':
            return "text-info";
        default:
            return "text-muted";
    }
}


export const extractOrigin = (url: string): string | null => {
    try {
        const parsedUrl = new URL(url);
        let hostname = parsedUrl.hostname;

        // Remove 'www.' if present
        if (hostname.startsWith('www.')) {
            hostname = hostname.slice(4);
        }

        return hostname;
    } catch (error) {
        console.error('Invalid URL:', error);
        return null;
    }
}

export const generateUniqueKey = (obj: any, exclude: string[] = []): string => {
    const sortedKeys = Object.keys(obj).filter((k) => !exclude.includes(k)).sort();
    const sortedObjString = sortedKeys.map(key => `${key}:${JSON.stringify(obj[key])}`).join('|');

    const hash = CryptoJS.SHA256(sortedObjString).toString(CryptoJS.enc.Hex);
    return hash;
}

export const generateGoogleMapUrl = (
    lat: number,
    lng: number,
    placeId: string | null = null,
    name: string = 'Location'
): string => {
    if (placeId) {
        const baseUrl = 'https://www.google.com/maps/search/';
        const params = new URLSearchParams({
            api: '1',
            query: name,
            query_place_id: placeId,
        });
        return `${baseUrl}?${params.toString()}`;
    }

    return `https://www.google.com/maps?q=${lat},${lng}`;
};


export const getCombinedAddress = (address: SavedAddress | undefined | null) => {

    if (address) {
        const combinedAddress = `${address.premise ? address.premise + ',' : ''} ${address.sublocality ? address.sublocality + ',' : ''} ${address.city ? address.city + ',' : ''} ${address.region ? address.region + ',' : ''} ${address.pincode}`;
        return combinedAddress;
    }

    return "";
}

export async function retryUntil(
    checkFn: () => Promise<boolean>,
    maxAttempts: number = 3,
    interval: number = 10000
): Promise<boolean> {
    for (let i = 0; i < maxAttempts; i++) {
        const success = await checkFn();
        if (success) return true;

        if (i < maxAttempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, interval));
        }
    }
    return false;
}

// Base64 encode a JS object using UTF-8 encoding
export const encodeBase64 = (obj: any): string => {
    const json = JSON.stringify(obj);
    const bytes = new TextEncoder().encode(json);
    const base64 = btoa(String.fromCharCode(...Array.from(bytes)));
    return base64;
};

// Base64 decode to JS object using UTF-8 decoding
export const decodeBase64 = (base64: string, fallbackValue: any = {}): any => {
    try {
        const binary = atob(base64);
        const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
        const json = new TextDecoder().decode(bytes);
        return JSON.parse(json);
    } catch (error) {
        return fallbackValue;
    }
};

export const getFiltersQuery = (data: any) => {
    const encodedData = encodeBase64({ ...data });
    return `?filters=${encodedData}`;
}

export const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () =>
            resolve((reader.result as string).split(',')[1]); // only base64 part
        reader.readAsDataURL(blob);
    });
};
export function maskString(str: string, start: number, end: number, maskChar: string = '*'): string {
    if (!str) return '';
    if (start < 0 || end > str.length || start >= end) return str;
  
    const prefix = str.slice(0, start);
    const masked = maskChar.repeat(end - start);
    const suffix = str.slice(end);
  
    return prefix + masked + suffix;
}

export type MobileOS = "android" | "ios" | null;

export function getMobileOSIfWeb(): MobileOS {

  if (isNativePlatform()) {
    return null;
  }

  const ua = navigator.userAgent || navigator.vendor;

  if (/android/i.test(ua)) {
    return "android";
  }

  if (/iPad|iPhone|iPod/i.test(ua)) {
    return "ios";
  }

  return null;
}


export const addAppDownloadUTMParams = (url: string): string => {
    try {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        params.set('utm_source', 'web');
        params.set('utm_medium', 'organic');
        params.set('utm_campaign', 'app_download');
        urlObj.search = params.toString();
        return urlObj.toString();
    } catch (error) {
        return url;
    }
}

export const isDateMoreThanDaysAgo = (date: string, days: number) => {
    if (!date) return false;
    const dateObj = new Date(date.split(' ')[0]);
    if (isNaN(dateObj.getTime())) return false;
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - dateObj.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > days;
}


export const formatAmount = (
    amount: number | string,
    currency: string = "INR",
    locale: string = "en-IN"
): string => {
    const value = typeof amount === "string" ? Number(amount) : amount;

    if (isNaN(value)) return "₹0";

    return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    }).format(value);
};



export function formatDateRelative(dateInput: Date | string): string {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    const now = new Date();

    // Remove time parts for both dates
    const simpleDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const simpleNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = simpleDate.getTime() - simpleNow.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    if (diffDays === 0) {
        return "Today";
    } else if (diffDays === -1) {
        return "Yesterday";
    } else {
        // Get ordinal suffix for the day
        const day = date.getDate();
        let suffix = "th";
        if (day % 10 === 1 && day !== 11) suffix = "st";
        else if (day % 10 === 2 && day !== 12) suffix = "nd";
        else if (day % 10 === 3 && day !== 13) suffix = "rd";
        const month = date.toLocaleString("en-US", { month: "short" });
        const year = date.getFullYear();
        return `${day}${suffix} ${month} ${year}`;
    }
}
